import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useConfig, usePositions, useStaticData, useBaseData, useUnlocodeRegister, useCountries } from '../hooks/useVessels';
import { useFavorites } from '../hooks/useFavorites';
import { Spinner } from '../components/Spinner';
import { Sidebar } from '../components/Sidebar';
import { Map } from '../components/Map';
import { FilterBar } from '../components/FilterBar';
import { SearchOverlay } from '../components/SearchOverlay';
import type { VesselFilter } from '../types/vessel';

export function MapView() {
  const navigate = useNavigate();
  const { data: config, isLoading: configLoading } = useConfig();
  const { data: vessels = {}, isLoading: vesselsLoading } = usePositions();
  const { data: staticData = {} } = useStaticData();
  const { data: baseData = {} } = useBaseData();
  const { data: unlocodeRegister = [] } = useUnlocodeRegister();
  const { data: countries = [] } = useCountries();

  const { favorites, hiddenGroups, emptyGroups, toggleFavorite, toggleGroupVisibility, moveToGroup, createGroup, renameGroup, deleteGroup } = useFavorites();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<VesselFilter>('');
  const [labelsVisible, setLabelsVisible] = useState(false);
  const [searchMatches, setSearchMatches] = useState<string[] | null>(null);
  const [flyToVesselId, setFlyToVesselId] = useState<string | null>(null);

  const counts = useMemo(() => {
    const vesselIds = Object.keys(vessels);
    let total = 0, lng = 0, bunker = 0, favs = 0;
    vesselIds.forEach((id) => {
      if (favorites[id] && hiddenGroups[favorites[id].group]) return;
      total++;
      const type = baseData[id]?.Type || '';
      if (type === 'LNG') lng++;
      else if (type === 'Bunkervessel') bunker++;
      if (favorites[id]) favs++;
    });
    return { total, lng, bunker, favorites: favs };
  }, [vessels, baseData, favorites, hiddenGroups]);

  const handleToggleFavorite = useCallback((id: string) => {
    const vessel = vessels[id];
    if (vessel) toggleFavorite(id, vessel, baseData[id], staticData[id]);
  }, [vessels, baseData, staticData, toggleFavorite]);

  const handleSearch = useCallback((_query: string, matches: string[]) => {
    setSearchMatches(matches.length > 0 ? matches : null);
  }, []);

  const handleFlyToVessel = useCallback((id: string) => {
    setFlyToVesselId(id);
    setTimeout(() => setFlyToVesselId(null), 2000);
  }, []);

  if (configLoading || vesselsLoading) {
    return <Spinner message="Loading vessel data..." />;
  }

  if (!config?.mapboxToken) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column' }}>
        <p style={{ color: '#333' }}>Failed to load configuration</p>
        <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>Make sure the API is running on port 8080</p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        favorites={favorites}
        hiddenGroups={hiddenGroups}
        emptyGroups={emptyGroups}
        onToggleFavorite={handleToggleFavorite}
        onToggleGroupVisibility={toggleGroupVisibility}
        onFlyToVessel={handleFlyToVessel}
        onSwitchToTable={() => navigate('/table')}
        onCreateGroup={createGroup}
        onMoveToGroup={moveToGroup}
        onRenameGroup={renameGroup}
        onDeleteGroup={deleteGroup}
        vessels={vessels}
        baseData={baseData}
        staticData={staticData}
      />

      <Map
        mapboxToken={config.mapboxToken}
        vessels={vessels}
        baseData={baseData}
        staticData={staticData}
        favorites={favorites}
        hiddenGroups={hiddenGroups}
        currentFilter={currentFilter}
        searchMatches={searchMatches}
        labelsVisible={labelsVisible}
        unlocodeRegister={unlocodeRegister}
        countries={countries}
        flyToVesselId={flyToVesselId}
        sidebarCollapsed={sidebarCollapsed}
        onToggleFavorite={handleToggleFavorite}
      />

      <SearchOverlay
        vessels={vessels}
        baseData={baseData}
        staticData={staticData}
        onSearch={handleSearch}
        onFlyToVessel={handleFlyToVessel}
        sidebarCollapsed={sidebarCollapsed}
      />

      <FilterBar
        currentFilter={currentFilter}
        onFilterChange={setCurrentFilter}
        counts={counts}
        labelsVisible={labelsVisible}
        onToggleLabels={() => setLabelsVisible(!labelsVisible)}
      />

      <div style={{ 
        position: 'fixed', 
        bottom: '28px', 
        right: '12px', 
        zIndex: 100, 
        pointerEvents: 'none',
        background: 'rgba(255, 255, 255, 0.8)',
        padding: '8px 12px',
        borderRadius: '6px'
      }}>
        <img src="/img/ofiniti_logo.svg" alt="Ofiniti" style={{ height: '28px', display: 'block' }} />
      </div>
    </div>
  );
}
